import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Menu, X, User, LogOut, Settings, BookOpen, Zap, Phone } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";

export const Navbar = ({ onRegisterClick }) => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img src={HFS_LOGO} alt="Hire For Skillz" className="h-10 w-10 object-contain" />
            <span className="font-heading font-bold text-xl text-hfs-navy group-hover:text-hfs-navy-dark transition-colors">
              Hire For Skillz
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-hfs-navy font-medium transition-colors">
              Home
            </Link>
            <a href="#candidates" className="text-slate-600 hover:text-hfs-navy font-medium transition-colors">
              Candidates
            </a>
            <Link 
              to="/learn-communication" 
              className="flex items-center gap-1.5 text-slate-600 hover:text-hfs-navy font-medium transition-colors"
              data-testid="learn-communication-nav"
            >
              <BookOpen className="h-4 w-4" />
              Learn Communication
            </Link>
            <Link 
              to="/placement-ai" 
              className="flex items-center gap-1.5 text-slate-600 hover:text-amber-600 font-medium transition-colors"
              data-testid="placement-ai-nav"
            >
              <Zap className="h-4 w-4" />
              Placement AI
            </Link>
            <Link 
              to="/cold-call-lab" 
              className="flex items-center gap-1.5 text-slate-600 hover:text-cyan-600 font-medium transition-colors"
              data-testid="cold-call-lab-nav"
            >
              <Phone className="h-4 w-4" />
              Cold Call Lab
            </Link>
            <a href="#about" className="text-slate-600 hover:text-hfs-navy font-medium transition-colors">
              About
            </a>
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="max-w-[120px] truncate">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm text-slate-500">
                    {user?.email}
                  </div>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-panel-link">
                        <Settings className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-hfs-navy" data-testid="login-nav-btn">
                    Login
                  </Button>
                </Link>
                <Button 
                  onClick={onRegisterClick}
                  className="bg-hfs-navy hover:bg-hfs-navy-dark text-white"
                  data-testid="register-nav-btn"
                >
                  Register
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-slate-600 hover:text-hfs-navy font-medium" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>
              <a href="#candidates" className="text-slate-600 hover:text-hfs-navy font-medium" onClick={() => setMobileMenuOpen(false)}>
                Candidates
              </a>
              <Link 
                to="/learn-communication" 
                className="flex items-center gap-1.5 text-slate-600 hover:text-hfs-navy font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="h-4 w-4" />
                Learn Communication
              </Link>
              <Link 
                to="/placement-ai" 
                className="flex items-center gap-1.5 text-slate-600 hover:text-amber-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Zap className="h-4 w-4" />
                Placement AI
              </Link>
              <Link 
                to="/cold-call-lab" 
                className="flex items-center gap-1.5 text-slate-600 hover:text-cyan-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Phone className="h-4 w-4" />
                Cold Call Lab
              </Link>
              <a href="#about" className="text-slate-600 hover:text-hfs-navy font-medium" onClick={() => setMobileMenuOpen(false)}>
                About
              </a>
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" className="text-slate-600 hover:text-hfs-navy font-medium" onClick={() => setMobileMenuOpen(false)}>
                      Admin Panel
                    </Link>
                  )}
                  <button onClick={handleLogout} className="text-left text-red-600 hover:text-red-700 font-medium">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-600 hover:text-hfs-navy font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Login
                  </Link>
                  <Button 
                    onClick={() => { onRegisterClick(); setMobileMenuOpen(false); }}
                    className="bg-hfs-navy hover:bg-hfs-navy-dark text-white w-fit"
                  >
                    Register
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
